/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import QueryDetailLink, {
  buildQueryDetailTarget,
  QueryDetailEditAction,
} from '../components/QueryDetailLink';
import TotalCTR from 'components/kea/widgets/popularQueries/TotalCTR';
import AnalyticTable from 'components/kea/tables/AnalyticTable';
import CardContainer from 'components/kea/CardContainer';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import AnalyticsMetricBar from '../components/AnalyticsMetricBar';
import AnalyticsReportActions from '../components/AnalyticsReportActions';
import {
  loadAnalyticsReport,
  selectTopQueries,
  selectTimeframe,
  actions as analyticsActions,
} from 'slices/AnalyticsSlice';
import { rulesForQuery, useQueryRuleIndex } from '../components/useQueryRuleIndex';

/**
 * Bridge-backed Top Queries page.
 *
 * Column labels use "Searches" (not "Users") because the plugin analytics table
 * has no user identity — event counts only.
 * The `refine` column is omitted — it is always 0 in the plugin model.
 *
 * Sidebar: only TotalCTR (click events / search events — honest event-level ratio).
 * TotalQueries is intentionally excluded: its widget copy says "Number of queries" /
 * "Total number of search sessions" but the backend value is `$totalSearches` (event
 * count), which is neither distinct queries nor sessions.
 */
const QueryPopularBridge = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const localizeMessage = useLocalizedMessage();
  const topQueries = useSelector(selectTopQueries);
  const timeframe = useSelector(selectTimeframe);
  const { currentShop } = useSelector((store) => store.app);
  const { capabilities } = useShellAppContext();
  const queryRuleIndex = useQueryRuleIndex();
  const detailEnabled = Boolean(capabilities?.analyticsQueryDetailV1);
  const exportEnabled = Boolean(capabilities?.analyticsReportExportV1);
  const returnTo = `${location.pathname}${location.search}`;

  const { isFetching, data, error } = topQueries;
  const maximumCtr = Math.max(100, ...data.map((row) => Number(row.ctr || 0) * 100));
  const maximumSearches = Math.max(1, ...data.map((row) => Number(row.searches || 0)));

  useEffect(() => {
    if (!currentShop) return;
    dispatch(loadAnalyticsReport({ shopId: currentShop, reportType: 'top_queries' }));
  }, [dispatch, currentShop, timeframe]);

  const handleTimeframeChange = (range) => {
    dispatch(analyticsActions.setTimeframe(range));
  };

  const headCells = [
    {
      id: 'query', numeric: false, label: localizeMessage('analytics.table.query'), sortKey: 'queryText',
    },
    {
      id: 'searches',
      numeric: true,
      label: localizeMessage('analytics.table.searches'),
      render: (value) => (
        <AnalyticsMetricBar
          value={value}
          maximum={maximumSearches}
          label={localizeMessage('analytics.table.searches')}
          tone="searches"
          compact
        />
      ),
      compact: true,
      width: 112,
    },
    {
      id: 'click',
      numeric: true,
      label: localizeMessage('analytics.table.click'),
      compact: true,
      width: 76,
    },
    {
      id: 'noaction',
      numeric: true,
      label: localizeMessage('analytics.table.noAction'),
      compact: true,
      width: 88,
    },
    {
      id: 'ctr',
      numeric: true,
      label: localizeMessage('analytics.table.ctr'),
      render: (value) => (
        <AnalyticsMetricBar
          value={value}
          maximum={maximumCtr}
          valueFormatter={(metric) => `${metric.toFixed(1)}%`}
          label={localizeMessage('analytics.table.ctr')}
          tone="clickRate"
          compact
        />
      ),
      compact: true,
      width: 112,
    },
    detailEnabled && {
      id: 'edit',
      numeric: false,
      label: localizeMessage('analytics.table.settings'),
      visuallyHiddenLabel: true,
      action: true,
      sortable: false,
      width: 64,
    },
  ].filter(Boolean);

  const prepareRows = (rows) => rows.map((el, index) => ({
    ...el,
    queryText: el.query,
    detailTarget: detailEnabled
      ? buildQueryDetailTarget(currentShop, el.query, returnTo)
      : '',
    query: (
      <QueryDetailLink
        shopId={currentShop}
        query={el.query}
        enabled={detailEnabled}
        activeRuleCount={rulesForQuery(queryRuleIndex, el.query).active.length}
      />
    ),
    edit: (
      <QueryDetailEditAction
        shopId={currentShop}
        query={el.query}
        enabled={detailEnabled}
      />
    ),
    ctr: Number((el.ctr * 100).toFixed(1)),
    id: `${el.query}-${index}`,
  }));

  const handleRowActivate = (row) => {
    if (row.detailTarget) history.push(row.detailTarget);
  };

  return (
    <SearchFeaturePageShell
      pageId="analyticQueryPopular"
      actions={(
        <AnalyticsReportActions
          exportEnabled={exportEnabled}
          shopId={currentShop}
          reportType="top_queries"
          timeframe={timeframe}
          onSubmitRange={handleTimeframeChange}
          isFetching={isFetching}
          localizeMessage={localizeMessage}
        />
      )}
    >
      <Grid spacing={4} container>
        <Grid item xs={12} sm={12} md={12} lg={9}>
          <CardContainer
            headerId="search.analytics.queryPopular.header"
            styleName="noMargin"
            tableSurface
            isFetching={isFetching}
            error={error}
          >
            <AnalyticTable
              rows={prepareRows(data)}
              headCells={headCells}
              orderById="searches"
              fixedLayout
              interactiveRows={detailEnabled}
              onRowActivate={handleRowActivate}
              minWidth={720}
            />
          </CardContainer>
        </Grid>

        <Grid item xs={12} sm={12} md={12} lg={3}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <TotalCTR queryPopular={topQueries} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </SearchFeaturePageShell>
  );
};

export default QueryPopularBridge;
